// 표시 모드(InputWidget.mode)에서 **지금은 소비되지 않는 속성**의 공통 표기.
//
// 속성 패널의 여러 축이 같은 사정을 각자 다른 문구로 알리면(또는 아무 말도 없으면) 저작자가
// "지정했는데 왜 안 되지"를 축마다 다시 배운다. 여기 한 벌로 모은다.
import type { ReactElement, ReactNode } from 'react';
import type { ComposerWidget, InputWidget } from '@safetyhub/shared';

/** 작성폼 위젯 표시 모드 — 작성 페이지에서의 노출 방식(속성 패널 버튼 라벨 정본). */
export const WRITE_FORM_MODE_OPTIONS: {
  value: 'editable' | 'readonly' | 'hidden';
  label: string;
}[] = [
  { value: 'editable', label: '편집' },
  { value: 'readonly', label: '읽기전용' },
  { value: 'hidden', label: '숨김' },
];

/** 이 축이 동작하려면 위젯이 어떤 표시 모드여야 하는가. */
export type WidgetModeNeed = 'editable' | 'readonly';

/**
 * 이 위젯의 현재 표시 모드에서 `needs` 축이 죽어 있는가 — rc 렌더 분기와 같은 판정.
 *
 * 근거(packages/report-composer/src/composerWidgetTreeRender.tsx): `mode:'readonly'`면 렌더러가
 * 위젯별 case에 닿기 전에 값 표시로 빠지고(`node.mode === 'readonly' &&
 * !READONLY_SELF_RENDER_WIDGETS.has(node.widget)` 조기 분기), `'hidden'`이면 아예 null을 돌린다.
 * 그래서 **입력 컨트롤을 전제로 한 축**(검색 제안·입력 형식·편집 진입·활성 조건)은 그 두 모드에서
 * 읽히는 코드 경로 자체가 없다. 반대로 **읽기 표시 전용 축**(표시 형식)은 그 조기 분기 안에서만
 * 소비돼 편집 모드에서 무의미하다 — 두 방향을 `needs`로 구분한다.
 *
 * ⚠️ 모드로 무의미해지지 **않는** 축은 이 게이트에 태우지 말 것. 읽기 전용에서도 소비되는 축이
 * 있다: select/radio 옵션(코드→라벨 변환 — readonlyDisplayValue), 기본값(collectWidgetDefaultValues가
 * 모드 무관 시드), 안내 문구·접기·표시 조건·라벨 병기, 그리고 **역전파**(서버
 * collectWriteBackWidgets가 mode를 보지 않아 읽기 전용 칸의 값도 그대로 정본 저장소에 기록된다).
 */
export function isWidgetModeInert(w: ComposerWidget, needs: WidgetModeNeed): boolean {
  return w.kind === 'input' && (w.mode ?? 'editable') !== needs;
}

/** 선택 위젯 중 이 축에 저장된 값이 하나라도 있는가 — 잠금 여부의 기준(값 있으면 잠그지 않는다). */
export function anyWidgetHasValue(
  widgets: ComposerWidget[],
  has: (w: InputWidget) => boolean,
): boolean {
  return widgets.some((w) => w.kind === 'input' && has(w));
}

/**
 * 무의미해진 축의 컨트롤을 감싸 사정을 알리는 공용 게이트.
 *
 * 표기 규약 — **감추지 않는다**(저장된 값이 유령이 되면 저작자가 "그런 기능이 없다"고 잘못 배운다):
 *  - 무의미 + **값 없음** → 컨트롤 비활성 + 사유·해법 한 줄(지금 켜 봐야 동작하지 않으므로)
 *  - 무의미 + **값 있음** → 잠그지 않고 경고만. 잠그면 지울 수도 없는 막다른 길이 된다
 *  - 복수 선택 **일부**만 무의미 → 잠그지 않고 몇 개가 그런지 알린다
 */
export function WidgetModeInertGate({
  widgets,
  needs,
  hasValue,
  children,
}: {
  widgets: ComposerWidget[];
  needs: WidgetModeNeed;
  hasValue: boolean;
  children: ReactNode;
}): ReactElement {
  const inputs = widgets.filter((w) => w.kind === 'input');
  const inertCount = inputs.filter((w) => isWidgetModeInert(w, needs)).length;
  const allInert = inputs.length > 0 && inertCount === inputs.length;
  // 값이 있으면 절대 잠그지 않는다 — 잠긴 컨트롤은 지울 수도 없어 되돌릴 길이 사라진다.
  const lock = allInert && !hasValue;
  const needLabel = WRITE_FORM_MODE_OPTIONS.find((o) => o.value === needs)?.label ?? needs;
  return (
    <>
      {/* fieldset[disabled]가 안의 네이티브 컨트롤을 통째로 잠근다(축마다 disabled를 배선하지
          않아도 되는 이유). div로 짠 콤보박스류는 네이티브가 아니라 pointer-events로 함께 막고
          aria-disabled로 보조기술에도 알린다. min-w-0은 fieldset 기본 min-width:min-content 상쇄. */}
      <fieldset
        disabled={lock}
        aria-disabled={lock || undefined}
        className={lock ? 'min-w-0 pointer-events-none opacity-60' : 'min-w-0'}
      >
        {children}
      </fieldset>
      {lock ? (
        <p className="mt-1 text-[11px] leading-snug text-gray-400">
          표시 모드가 &apos;{needLabel}&apos;이 아니라 이 설정은 동작하지 않습니다 — 표시 모드를
          &apos;{needLabel}&apos;으로 바꾸면 설정할 수 있습니다.
        </p>
      ) : allInert ? (
        <p className="mt-1 text-[11px] leading-snug text-amber-600">
          표시 모드가 &apos;{needLabel}&apos;이 아니라 지금은 적용되지 않습니다 — 표시 모드를
          &apos;{needLabel}&apos;으로 바꾸면 이 설정대로 동작합니다.
        </p>
      ) : inertCount > 0 ? (
        <p className="mt-1 text-[11px] leading-snug text-amber-600">
          선택한 위젯 중 {inertCount}개는 표시 모드가 &apos;{needLabel}&apos;이 아니라 지금은
          적용되지 않습니다.
        </p>
      ) : null}
    </>
  );
}
